import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material'
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useProgramEngagementContext } from '../../../../context'
import { RequiredDocumentsAPI } from '../../../ResourceDetailFeatures/EQIP/RequiredDocumentsAPI'

export const Aop1236Preview = () => {
  const { t } = useTranslation('@cibo/programs/Aop1236Preview')
  const { engagementId, taskStatus } = useProgramEngagementContext()

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['aop1236Preview', engagementId, taskStatus?.status],
    queryFn: () =>
      RequiredDocumentsAPI.previewAopForm1236({ engagementId: engagementId as string }),
    enabled: !!engagementId,
  })

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
        <CircularProgress />
      </Stack>
    )
  }

  if (isError) {
    const status = (error as any)?.response?.status
    if (status === 404) {
      return <Typography color="text.secondary">{t('noData')}</Typography>
    }
    return (
      <Alert severity="error">
        {t('error')} {error instanceof Error ? error.message : 'Unknown error'}
      </Alert>
    )
  }

  if (!data?.contractFormData) {
    return <Alert severity="info">{t('noData')}</Alert>
  }

  const contractData = data.contractFormData

  // Transform signing forms to table rows
  const tableRows = contractData.signingForms.map((form, index) => ({
    id: index,
    contractNumber: form.contractNumber,
    formType: form.formType,
    year: form.year,
    producerName: form.formFields.producerName,
    producerAddress: form.formFields.producerAddress,
    producerCounty: form.formFields.producerCounty,
    producerState: form.formFields.producerState,
    box15: form.formFields.box15,
    box16: form.formFields.box16,
    box17: form.formFields.box17,
  }))

  const signingFormsColumns: GridColDef[] = [
    {
      field: 'formType',
      headerName: t('formType'),
      width: 150,
    },
    {
      field: 'year',
      headerName: t('year'),
      type: 'number',
      width: 80,
    },
    {
      field: 'producerName',
      headerName: t('producerName'),
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'producerAddress',
      headerName: t('producerAddress'),
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'producerCounty',
      headerName: t('producerCounty'),
      width: 130,
    },
    {
      field: 'producerState',
      headerName: t('producerState'),
      width: 100,
    },
    {
      field: 'box15',
      headerName: t('box15'),
      width: 80,
    },
    {
      field: 'box16',
      headerName: t('box16'),
      width: 100,
    },
    {
      field: 'box17',
      headerName: t('box17'),
      flex: 2,
      minWidth: 200,
    },
  ]

  return (
    <Stack spacing={3}>
      {/* Signing Forms Section */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('signingForms')}
        </Typography>
        <Box sx={{ height: Math.max(300, tableRows.length * 52 + 56) }}>
          <DataGridPro
            columns={signingFormsColumns}
            rows={tableRows}
            density="compact"
            disableRowSelectionOnClick
            hideFooterPagination={tableRows.length <= 10}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10, page: 0 } },
            }}
          />
        </Box>
      </Box>

      {/* Signer Information Section */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('signerInformation')}
        </Typography>
        <Stack spacing={2}>
          <SignerInfoRow
            label={t('advisor')}
            name={contractData.advisorSignerName}
            email={contractData.advisorSignerEmail}
          />
          <SignerInfoRow
            label={t('cibo')}
            name={contractData.ciboSignerName}
            email={contractData.ciboSignerEmail}
          />
          <SignerInfoRow
            label={t('participant')}
            name={contractData.participantSignerName}
            email={contractData.participantSignerEmail}
          />
        </Stack>
      </Box>
    </Stack>
  )
}

interface SignerInfoRowProps {
  label: string
  name: string
  email: string
}

const SignerInfoRow = ({ label, name, email }: SignerInfoRowProps) => {
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        p: 2,
        backgroundColor: 'background.default',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ minWidth: 120 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2">{name}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {email}
        </Typography>
      </Box>
    </Stack>
  )
}
